import * as React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AuthService from '../services/auth';
import { alpha } from '@mui/material/styles';

import AppConfig from '../services/app-config';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import { Navigate, useNavigate } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import PlumbingOutlinedIcon from '@mui/icons-material/PlumbingOutlined';


import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import NoCrashIcon from '@mui/icons-material/NoCrash';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventIcon from '@mui/icons-material/Event';
import FilterListIcon from '@mui/icons-material/FilterList';


import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { CardActions } from '@mui/material';

import DriveEtaIcon from '@mui/icons-material/DriveEta';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

import PageHeader from '../components/PageHeader';
import SolarIcon from '../icons/SolarIcon';

export default function App({setNavbarState, navbarState}) {
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' })
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const navigate = useNavigate();
  
  // Get date from query param if available
  const getInitialDate = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const dateParam = searchParams.get('date');
    
    if (dateParam && dateParam.length === 6) {
      try {
        // Parse date from ddmmyy format
        const day = dateParam.substring(0, 2);
        const month = dateParam.substring(2, 4);
        const year = '20' + dateParam.substring(4, 6); // Assuming 20xx year
        
        // Create date in YYYY-MM-DD format for dayjs
        const dateString = `${year}-${month}-${day}`;
        const parsedDate = dayjs(dateString);
        
        // Check if valid date
        if (parsedDate.isValid()) {
          return parsedDate;
        }
      } catch (error) {
        console.error('Error parsing date from URL:', error);
      }
    }
    
    // Default to current date if no valid date param
    return dayjs();
  };
  
  const [date, setDate] = React.useState(getInitialDate());
  const [carVisits, setCarVisits] = React.useState(0);
  const [walkingVisits, setWalkingVisits] = React.useState(0);
  const [events, setEvents] = React.useState([]);
  const [searchValue, setSearchValue] = React.useState("");
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [advancedSearch, setAdvancedSearch] = React.useState(false);
  const [fromDate, setFromDate] = React.useState(dayjs());
  const [toDate, setToDate] = React.useState(dayjs());
  const apiUrl = process.env.REACT_APP_MGO_API_URL;
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [showIdBackdrop, setShowIdBackdrop] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [idDialog,setIdDialog] = React.useState({show: false, id: null, title: ""});
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const [isPanning, setIsPanning] = React.useState(false);
  const [panPosition, setPanPosition] = React.useState({ x: 0, y: 0 });

  
  const filteredEvents = events.filter(event => {
    // Always include date separators
    if (event.isDateSeparator) {
      return true;
    }
    
    // Filter regular events based on search value
    const searchLower = searchValue.toLowerCase();
    return Object.keys(event).some(key => {
      if (typeof event[key] === 'string') {
        return event[key].toLowerCase().includes(searchLower);
      } else if (typeof event[key] === 'object' && event[key] !== null) {
        return Object.values(event[key]).some(value => 
          typeof value === 'string' && value.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  });

  // For debugging
  console.log("Events with separators:", events.filter(e => e.isDateSeparator).length);
  console.log("Filtered events with separators:", filteredEvents.filter(e => e.isDateSeparator).length);

  const cardStyle = { 
    borderRadius: '10px',
    boxShadow: 0,
    border: '1px solid',
    borderColor: 'divider',
  }

  function Stats({ carVisits, walkingVisits }) {  
      return (
          <Box sx={{ mt: 0 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary', mb: 1.5 }}>
                  Estadísticas
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                  <Card sx={{ flex: 1, borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(125, 217, 158, 0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <DriveEtaIcon sx={{ color: '#238048' }} />
                          </Box>
                          <Box>
                              <Typography sx={{ fontSize: 20, fontWeight: 900, color: 'text.primary', lineHeight: 1.1 }}>{carVisits}</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Vehículos</Typography>
                          </Box>
                      </CardContent>
                  </Card>
                  <Card sx={{ flex: 1, borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '10px', backgroundColor: 'rgba(125, 217, 158, 0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <DirectionsWalkIcon sx={{ color: '#238048' }} />
                          </Box>
                          <Box>
                              <Typography sx={{ fontSize: 20, fontWeight: 900, color: 'text.primary', lineHeight: 1.1 }}>{walkingVisits}</Typography>
                              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Peatones</Typography>
                          </Box>
                      </CardContent>
                  </Card>
              </Box>
          </Box>
      )
  }
  const closeIdDialog = () => {
    setIdDialog({show: false, id: null, title: ""});
    setImageLoaded(false);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }

  const openDialog = (id, visitor_name, validationData) => {
    setShowIdBackdrop(true);
    setIdDialog({show: true, id: id, title: visitor_name, validationData: validationData});
  }

  function downloadImage(imageId) {
    const downloadUrl = `${apiUrl}/img/id/${imageId}.jpg`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `image_${imageId}.jpg`;
    link.click();
  }

  function parksharingEvent(event, index) {
    const icon = event.event === "parqueo solicitado" ? <CarCrashIcon sx={{ color: 'text.primary' }} /> : <NoCrashIcon sx={{ color: 'text.primary' }} />;
    return (
      <Card key={index} sx={cardStyle}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '999px', backgroundColor: 'rgba(125, 217, 158, 0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {AppConfig.titleCase(event.event)}
                </Typography>
                {event.requester_unit && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{event.requester_unit}</Typography>
                )}
              </Box>
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
              {AppConfig.formatHour(event.ts)}
            </Typography>
          </Box>
          <Box sx={{ ml: 6.5, mt: 0.5 }}>
            {event.event === "parqueo solicitado" && (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                  <b>{event.username}</b> ha solicitado un parqueo.
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Desde {AppConfig.formatFriendlyDate(event.time_start).replace("hoy a las","las")} hasta {AppConfig.formatFriendlyDate(event.time_end).replace("hoy a las","las")}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Vehiculo {event.plates}</Typography>
              </>
            )}
            {event.event === "parqueo compartido" && (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                  <b>{event.username}</b> ha prestado su parqueo a {event.requester}.
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Parqueo {event.parking}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Vehiculo {event.plates}</Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  function amenityEvent(event, index) {
    const iconMap = {
      "reservacion creada": <EventIcon sx={{ color: 'text.primary' }} />,
      "reservacion aprobada": <EventAvailableIcon sx={{ color: 'text.primary' }} />,
      "pre-reservacion creada": <EventRepeatIcon sx={{ color: 'text.primary' }} />,
      "reservacion cancelada": <EventBusyIcon sx={{ color: 'text.primary' }} />,
    };
    const titleMap = {
      "reservacion cancelada": "Reservacion Cancelada",
      "reservacion aprobada": "Reservacion Aprobada",
    };
    return (
      <Card key={index} sx={cardStyle}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '999px', backgroundColor: 'rgba(125, 217, 158, 0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {iconMap[event.event] || <EventIcon sx={{ color: 'text.primary' }} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {titleMap[event.event] || "Reservacion Creada"}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                  {event.data.unidad} | {event.data.amenidad}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
              {AppConfig.formatHour(event.ts)}
            </Typography>
          </Box>
          <Box sx={{ ml: 6.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{event.by_name}</Typography>
            {event.event !== "reservacion cancelada" && event.event !== "reservacion aprobada" && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {event.data.confirmada ? "Confirmada" : "Pendiente de Confirmacion"}
              </Typography>
            )}
            {event.event === "reservacion aprobada" && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {AppConfig.getLocal("unit_type")} {event.data.unidad}
              </Typography>
            )}
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
              {AppConfig.formatFriendlyDate(event.data.inicio)} - {AppConfig.formatHour(event.data.fin)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  function accessEvent(event, index) {
    const getIcon = () => {
      if (event.id_data && event.id_data.face) {
        return (
          <img 
            src={`${apiUrl}/img/id/${event.image_id}-face.jpg`} 
            alt="Face ID" 
            style={{ width: '28px', height: '28px', borderRadius: '50%' }}
          />
        );
      }
      switch (event.event) {
        case "ingreso":
          return event.code.vehicle ? <SolarIcon name="garage" size={24} color="text.primary" /> : <SolarIcon name="users-group-rounded" size={24} color="text.primary" />;
        case "ingreso manual":
          return <SolarIcon name="users-group-rounded" size={24} color="text.primary" />;
        case "acceso creado":
          return <CheckBoxIcon sx={{ color: 'text.primary' }} />;
        default:
          return null;
      }
    };
    return (
      <Card key={index} sx={cardStyle}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '999px', backgroundColor: 'rgba(125, 217, 158, 0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {getIcon()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {AppConfig.titleCase(event.event)}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                  {event.code?.unit || ''}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
              {AppConfig.formatHour(event.ts)}
            </Typography>
          </Box>
          <Box sx={{ ml: 6.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{event.code?.visitor_name || 'Visita'}</Typography>
            {event.by_name && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {`${AppConfig.titleCase(event.event)} ${event.event === "ingreso" ? "dado" : ""} por ${event.by_name}`}
              </Typography>
            )}
            {event.code.vehicle_plate && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Placa: {event.code.vehicle_plate}</Typography>
            )}
          </Box>
          {(event.image_id || (event.image_id && event.id_data)) && (
            <Box sx={{ ml: 6.5, mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {event.image_id && event.id_data && (
                  <>
                    {!event.id_data.isAnIDCard && (
                      <Chip size="small" icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />} label="Imagen invalida" color="error" sx={{ fontWeight: 700, fontSize: 11 }} />
                    )}
                    {event.id_data.isAnIDCard && event.id_data.matchesNameConfidence > 0.75 && (
                      <Chip size="small" icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />} label="ID validado" color="success" sx={{ fontWeight: 700, fontSize: 11 }} />
                    )}
                    {event.id_data.isAnIDCard && event.id_data.matchesNameConfidence <= 0.75 && (
                      <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: 14 }} />} label="ID no corresponde" color="warning" sx={{ fontWeight: 700, fontSize: 11 }} />
                    )}
                  </>
                )}
              </Box>
              {event.image_id && (
                <Button 
                  size="small" 
                  variant='outlined' 
                  color="secondary" 
                  onClick={() => openDialog(event.image_id, event.code?.visitor_name || 'Visita', event.id_data || {})} 
                  sx={{ borderRadius: '10px', fontWeight: 700, fontSize: 12 }}
                >
                  Ver ID
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  function issueEvent(event, index) {
    const isCreated = event.event === 'reporte creado';
    const estado = event?.data?.estado || '';
    const unidad = event?.data?.unidad || '';
    const titulo = event?.data?.titulo || event?.ticket || 'Reporte';

    return (
      <Card key={index} sx={cardStyle}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '999px', backgroundColor: 'rgba(125, 217, 158, 0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <PlumbingOutlinedIcon sx={{ color: 'text.primary' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {isCreated ? 'Reporte Creado' : 'Reporte Actualizado'}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {unidad}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
              {AppConfig.formatHour(event.ts)}
            </Typography>
          </Box>

          <Box sx={{ ml: 6.5, mt: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
              {titulo}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {event.by_name ? `${event.by_name}` : ''}
              {estado ? ` • ${estado.replaceAll('_', ' ')}` : ''}
              {event.ticket ? ` • ${event.ticket}` : ''}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }


  function DateSeparator({ date }) {
    return (
      <Box sx={{ mt: 2.5, mb: 1.5, display: 'flex', alignItems: 'center' }}>
        <Divider sx={{ flex: 1 }} />
        <Chip
          label={date}
          variant="outlined"
          sx={{
            mx: 1.5,
            borderRadius: '10px',
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 700,
            fontSize: 12,
            backgroundColor: 'background.paper',
          }}
        />
        <Divider sx={{ flex: 1 }} />
      </Box>
    );
  }

  function EventList({ events }) {
    return (
      <Stack spacing={1.5} sx={{ mt: 0 }}>
        {filteredEvents.map((event, index) => {
          if (event.isDateSeparator) {
            return <DateSeparator key={`date-${index}`} date={event.formattedDate} />;
          } else if (event.event === "acceso creado" || event.event === "ingreso" || event.event === "ingreso manual") {
            return accessEvent(event, index);
          } else if (event.event === "parqueo solicitado" || event.event === "parqueo compartido") {
            return parksharingEvent(event, index);
          } else if ((event.event === "reservacion creada" || event.event === "pre-reservacion creada" || event.event === "reservacion cancelada" || event.event === "reservacion aprobada" )&& event.data) {
            return amenityEvent(event, index);
          } else if ((event.event === 'reporte creado' || event.event === 'reporte actualizado') && event.data) {
            return issueEvent(event, index);
          }
          return null;
        })}
      </Stack>
    );
  }

  

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleDateChange = (newDate) => {
    setDate(newDate);
  }

  const handleFromDateChange = (newDate) => {
    setFromDate(newDate);
    if (newDate && toDate && toDate.diff(newDate, 'month') > 6) {
      setToDate(newDate.add(6, 'month'));
    }
  }

  const handleToDateChange = (newDate) => {
    setToDate(newDate);
    if (newDate && fromDate && newDate.diff(fromDate, 'month') > 6) {
      setFromDate(newDate.subtract(6, 'month'));
    }
  }

  const toggleAdvancedSearch = () => {
    setAdvancedSearch(!advancedSearch);
    // Reset events when toggling to avoid confusion
    if (!advancedSearch) {
      setEvents([]);
    } else {
      // When going back to simple search, fetch for the selected date
      fetchEventsForDay(date);
    }
  }

  const processEvents = (resp_data) => {
    let vehicleTrueCount = 0;
    let vehicleFalseCount = 0;
    let allEvents = [];
    
    // Process each event and track stats
    const processEvent = (event) => {
      if (event.event === "acceso creado" || event.event === "ingreso" || event.event === "ingreso manual") {
        if (!event.code || typeof event.code !== 'object') {
          // Some log entries (notably "ingreso manual") store the access code as a string and
          // the visitor/unit information at the top level.
          event.code = {
            code: event.code || null,
            visitor_name: event.visitor_name || event.guest_name || null,
            unit: event.unit || null,
            vehicle: event.vehicle || false,
            walking: event.walking || false,
            vehicle_plate: event.vehicle_plate || null,
          };
        }
        // Backfill missing nested fields from top-level (keeps UI consistent)
        if (!event.code.visitor_name && (event.visitor_name || event.guest_name)) {
          event.code.visitor_name = event.visitor_name || event.guest_name;
        }
        if (!event.code.unit && event.unit) {
          event.code.unit = event.unit;
        }
        if (event.event === "ingreso") {
          if (event.code.vehicle) {
            vehicleTrueCount++;
          } else {
            vehicleFalseCount++;
          }
        }
        if (event.code.vehicle_plate === null) {
          event.code.vehicle_plate = "";
        }
        if (event.id_data) {
          if (event.id_data.extraInfo && event.id_data.extraInfo.MatchesNameConfidence) {
            event.id_data.matchesNameConfidence = event.id_data.extraInfo.MatchesNameConfidence;
          } else if (event.id_data.extraInfo && event.id_data.extraInfo.matchesNameConfidence) {
            event.id_data.matchesNameConfidence = event.id_data.extraInfo.matchesNameConfidence;
          }
        }
      }
    };
    
    // Handle simple array format (for single-day search)
    if (Array.isArray(resp_data)) {
      console.log('Processing simple array format with', resp_data.length, 'events');
      resp_data.forEach(event => processEvent(event));
      resp_data.sort((a, b) => {
        if (a.ts && b.ts) return b.ts - a.ts;
        return 0;
      });
      setCarVisits(vehicleTrueCount);
      setWalkingVisits(vehicleFalseCount);
      setEvents(resp_data);
      return;
    }
    
    // Handle advanced search format (grouped by date)
    if (typeof resp_data === 'object' && resp_data !== null) {
      console.log('Processing advanced search format with dates:', Object.keys(resp_data));
      
      const ddmmyyToDate = (ddmmyy) => {
        try {
          const day = parseInt(ddmmyy.substring(0, 2), 10);
          const month = parseInt(ddmmyy.substring(2, 4), 10) - 1;
          const year = 2000 + parseInt(ddmmyy.substring(4, 6), 10);
          return new Date(year, month, day);
        } catch (e) {
          return new Date(0);
        }
      };

      // Sort dates newest to oldest
      const sortedDates = Object.keys(resp_data).sort((a, b) => ddmmyyToDate(b) - ddmmyyToDate(a));
      
      console.log('Sorted dates:', sortedDates);
      
      // Process each date group
      sortedDates.forEach(date => {
        const dateData = resp_data[date];
        console.log('Processing date:', date, 'with categories:', Object.keys(dateData));
        
        // Count actual events for this date across all categories
        let eventCount = 0;
        for (const category in dateData) {
          if (Array.isArray(dateData[category])) {
            eventCount += dateData[category].length;
          }
        }
        
        if (eventCount === 0) {
          console.log('Skipping empty date:', date);
          return; // Skip this date entirely if no events
        }
        
        // Add a date separator marker with a distinct key, event.id, and a visual marker
        // These properties ensure it's treated uniquely in the UI
        const formattedDate = `${date.substring(0, 2)}/${date.substring(2, 4)}/20${date.substring(4, 6)}`;
        allEvents.push({
          isDateSeparator: true,
          id: `date-separator-${date}`,
          event: `separator-${date}`,
          date: date,
          formattedDate: formattedDate
        });
        console.log('Added date separator for', formattedDate);
        
        // Process all categories of logs for this date (access, other_activity, etc.)
        // Collect and sort within-day by ts (newest first)
        const dayEvents = [];
        Object.keys(dateData).forEach(category => {
          if (Array.isArray(dateData[category])) {
            console.log(`Processing ${dateData[category].length} events in category '${category}' for date ${date}`);
            dateData[category].forEach(event => {
              event.dateGroup = date;
              processEvent(event);
              dayEvents.push(event);
            });
          }
        });
        dayEvents.sort((a, b) => {
          if (a.ts && b.ts) return b.ts - a.ts;
          return 0;
        });
        allEvents.push(...dayEvents);
      });
      
      console.log('Total events after processing:', allEvents.length, 'with', 
                  allEvents.filter(e => e.isDateSeparator).length, 'separators');

      console.log('Events sorted by date and ts from newest to oldest');
    }

    // Update the events with the processed data
    console.log('Setting events array with length:', allEvents.length);
    setCarVisits(vehicleTrueCount);
    setWalkingVisits(vehicleFalseCount);
    setEvents(allEvents);
  };

  const fetchEventsForDay = async (selectedDate) => {
    try {
      setShowBackdrop(true);
      const response = await apiClient.get(`full/logs/${selectedDate.format('DDMMYY')}`, {}, { action: 'fetch_activity_list' });
      AppConfig.parseResponse(response);
      processEvents(response.data.data);
    } catch (error) {
      console.log(error);
      setSnackbarMessage('Error fetching events');
      setSnackbarOpen(true);
    } finally {
      setShowBackdrop(false);
    }
  };

  const fetchAdvancedSearch = async () => {
    try {
      setShowBackdrop(true);
      // Prepare query parameters
      const params = new URLSearchParams();
      if (searchValue) params.append('query', searchValue.toLowerCase());
      if (fromDate) params.append('from_date', fromDate.format('DDMMYY'));
      if (toDate) params.append('to_date', toDate.format('DDMMYY'));
      
      console.log('Fetching advanced search with params:', params.toString());
      const response = await apiClient.get(`full/logs/search?${params.toString()}`, {}, { action: 'fetch_activity_search' });
      AppConfig.parseResponse(response);
      
      // Check if response.data.data exists and handle it appropriately
      if (response.data && response.data.data) {
        console.log('Advanced search response:', response.data.data);
        processEvents(response.data.data);
        
        // Check if we got any results
        const dataObj = response.data.data;
        let hasResults = false;
        
        if (typeof dataObj === 'object' && dataObj !== null) {
          // For date-grouped results
          for (const date in dataObj) {
            const categories = dataObj[date];
            for (const category in categories) {
              if (Array.isArray(categories[category]) && categories[category].length > 0) {
                hasResults = true;
                break;
              }
            }
            if (hasResults) break;
          }
        }
        
        if (!hasResults) {
          setSnackbarMessage('No se encontraron resultados');
          setSnackbarOpen(true);
        }
      } else {
        // Handle case where data.data is missing or not as expected
        console.log('Unexpected response format:', response.data);
        processEvents([]);
        setSnackbarMessage('No se encontraron resultados');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.log(error);
      setSnackbarMessage('Error fetching advanced search results');
      setSnackbarOpen(true);
      // Reset events to empty array on error
      setEvents([]);
    } finally {
      setShowBackdrop(false);
    }
  };

  React.useEffect(() => {
    if (!AuthService.userRoleIncludes(["admin","receptor"])) {
      setRedirect({trigger: true, route: "/"})
    }
    setNavbarState({...navbarState, showBack: true, showOptions: false, showSearch: false})
    
    // Only fetch events for single day when not in advanced search mode
    if (!advancedSearch) {
      fetchEventsForDay(date);
    }
  }, [date, advancedSearch]);

  const handleImageLoad = () => {
    setShowIdBackdrop(false);
    setImageLoaded(true);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };
  
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsPanning(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && zoomLevel > 1) {
      setPanPosition(prev => ({
        x: prev.x + e.movementX,
        y: prev.y + e.movementY
      }));
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  return (
    <>
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && (
        <Navigate to={redirect.route} replace={true} />
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}}>
        <Alert onClose={handleClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 2,
            pb: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <PageHeader
              title="Actividad"
              icon="history"
              onBack={() => navigate(-1)}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField 
              fullWidth 
              size="small" 
              id='search' 
              label="Busqueda" 
              variant="outlined" 
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                style: { borderRadius: '8px'}
              }}
            />
            <IconButton 
              onClick={toggleAdvancedSearch} 
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: advancedSearch ? 'secondary.main' : 'divider',
                color: advancedSearch ? 'secondary.main' : 'text.primary',
                backgroundColor: advancedSearch ? (theme) => alpha(theme.palette.secondary.main, 0.08) : 'transparent',
              }}
              aria-label={advancedSearch ? "Búsqueda Simple" : "Búsqueda Avanzada"}
              title={advancedSearch ? "Búsqueda Simple" : "Búsqueda Avanzada"}
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"es"}>
            {!advancedSearch ? (
              <DatePicker 
                sx={{ width: '100%' }}
                value={date}
                label="Actividad del Dia"
                onChange={handleDateChange}
                maxDate={dayjs()}
                minDate={dayjs(AppConfig.get("starting_date"))}
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'text.primary' }}>
                  Búsqueda Avanzada
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <DatePicker
                    sx={{ flex: 1 }}
                    label="Desde"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    maxDate={dayjs()}
                    minDate={dayjs().subtract(6, 'month')}
                  />
                  <DatePicker
                    sx={{ flex: 1 }}
                    label="Hasta"
                    value={toDate}
                    onChange={handleToDateChange}
                    maxDate={dayjs()}
                    minDate={fromDate}
                  />
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                  Rango máximo: 6 meses
                </Typography>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  onClick={fetchAdvancedSearch}
                  sx={{ fontWeight: 800, borderRadius: '10px', py: 1.2 }}
                >
                  Buscar
                </Button>
              </Box>
            )}
          </LocalizationProvider>
          <Stats carVisits={carVisits} walkingVisits={walkingVisits}/>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary', mb: 1.5 }}>
              Registros
            </Typography>
            <EventList events={events} />
          </Box>
        </Box>
      </Box>
    </Container>
    <Dialog open={idDialog.show} onClose={closeIdDialog} fullWidth maxWidth="md">
      <DialogTitle>{idDialog.title}</DialogTitle>
      <DialogContent>
        <Backdrop open={showIdBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff', mt: 6 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {idDialog.id && (
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '10px' }}>
              <Box 
                sx={{
                  cursor: zoomLevel > 1 ? 'move' : 'default',
                  overflow: 'hidden',
                  height: '500px', // Fixed height for consistent zoom behavior
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  src={`${apiUrl}/img/id/${idDialog.id}.jpg`}
                  alt="Imagen de ID"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transformOrigin: 'center',
                    transition: isPanning ? 'none' : 'transform 0.2s',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                  onLoad={handleImageLoad}
                  hidden={!imageLoaded}
                />
              </Box>
              <Box sx={{ position: 'absolute', bottom: 10, right: 10, display: 'flex', gap: 1, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '20px', padding: '5px 10px' }}>
                <IconButton size="small" onClick={handleZoomIn} sx={{ color: 'white' }}>
                  <ZoomInIcon />
                </IconButton>
                <IconButton size="small" onClick={handleZoomOut} sx={{ color: 'white' }}>
                  <ZoomOutIcon />
                </IconButton>
                <IconButton size="small" onClick={handleResetZoom} sx={{ color: 'white' }}>
                  <ZoomOutMapIcon />
                </IconButton>
              </Box>
            </Box>
            {imageLoaded && idDialog.validationData && idDialog.validationData.Fullname && (
              <Box sx={{ mt: 2, mb: 1 }}>
                {!idDialog.validationData.isAnIDCard && (
                  <Typography variant="caption" sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center',
                    bgcolor: 'error.main',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '4px 10px',
                    fontWeight: 'medium'
                  }}>
                    <ErrorOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Imagen no es un documento válido
                  </Typography>
                )}
                {idDialog.validationData.isAnIDCard && idDialog.validationData.matchesNameConfidence > 0.75 && (
                  <Typography variant="caption" sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center',
                    bgcolor: 'success.main',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '4px 10px',
                    fontWeight: 'medium'
                  }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Identidad validada correctamente
                  </Typography>
                )}
                {idDialog.validationData && idDialog.validationData.isAnIDCard && idDialog.validationData.matchesNameConfidence <= 0.75 && (
                  <Typography variant="caption" sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center',
                    bgcolor: 'warning.main',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '4px 10px',
                    fontWeight: 'medium'
                  }}>
                    <WarningAmberIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Documento posiblemente no corresponde al visitante registrado
                  </Typography>
                )}
              </Box>
            )}
            {idDialog.validationData && idDialog.validationData.isAnIDCard && idDialog.validationData.Fullname && (
              <>
              <Divider sx={{ mt: 1, mb: 1}}/>
              <Box>
                <Typography variant="caption" sx={{ 
                  display: 'block', 
                  alignItems: 'center',
                  fontWeight: 'medium'
                }}>
                  <InfoIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {idDialog.validationData.Fullname}
                </Typography>
                <Typography variant="caption" sx={{ 
                  display: 'block', 
                  alignItems: 'center',
                  ml: 2.5
                }}>
                  {idDialog.validationData.IDNumber}
                </Typography>
              </Box>
              </>
              )}
          </Box>
          
        )}
      </DialogContent>
      <DialogActions sx={{ mt: -1, mb: 1}}>
      {imageLoaded && (<>
        <Button variant="text" color="primary" onClick={() => downloadImage(idDialog.id)} sx={{ mt: 2 }}>
          <DownloadIcon sx={{ mr: 0.5 }}/>
          Descargar
        </Button>
        <Button onClick={closeIdDialog} variant="outlined" color="primary" sx={{ mt: 2 }}>
          Cerrar
        </Button>
        </>)}
      </DialogActions>
    </Dialog>
    </>
  );
}